import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { attachFranchise, detachFranchise, listFranchisees, listFranchisors } from '../services/franchises';
import { dealerKeys } from './useDealersQuery';
import type { FranchiseListResponse } from '../types/dealers.types';

export const franchiseKeys = {
    franchisors: (dealerId: string, page: number, perPage: number) =>
        [...dealerKeys.all, dealerId, 'franchisors', page, perPage] as const,
    franchisorsAll: (dealerId: string) => [...dealerKeys.all, dealerId, 'franchisors'] as const,
    franchisees: (dealerId: string, page: number, perPage: number) =>
        [...dealerKeys.all, dealerId, 'franchisees', page, perPage] as const,
    franchiseesAll: (dealerId: string) => [...dealerKeys.all, dealerId, 'franchisees'] as const,
};

// This dealer (a franchise_dealer) -> the OEMs franchising it.
export const useFranchisors = (dealerId: string, page = 1, perPage = 20) =>
    useQuery<FranchiseListResponse>({
        queryKey: franchiseKeys.franchisors(dealerId, page, perPage),
        queryFn: () => listFranchisors(dealerId, page, perPage),
        enabled: !!dealerId,
    });

// This dealer (an oem) -> the franchise dealers it franchises.
export const useFranchisees = (oemId: string, page = 1, perPage = 20) =>
    useQuery<FranchiseListResponse>({
        queryKey: franchiseKeys.franchisees(oemId, page, perPage),
        queryFn: () => listFranchisees(oemId, page, perPage),
        enabled: !!oemId,
    });

interface FranchisePair {
    franchiseDealerId: string;
    oemId: string;
}

// Attach/detach both invalidate the franchisor list (fd side) AND the
// franchisee list (oem side) so whichever tab is open — OEMs on the
// franchise_dealer's page, or Franchises on the oem's page — refreshes.
const invalidateFranchisePair = (
    queryClient: ReturnType<typeof useQueryClient>,
    { franchiseDealerId, oemId }: FranchisePair,
): void => {
    queryClient.invalidateQueries({ queryKey: franchiseKeys.franchisorsAll(franchiseDealerId) });
    queryClient.invalidateQueries({ queryKey: franchiseKeys.franchiseesAll(oemId) });
};

export const useAttachFranchiseMutation = () => {
    const queryClient = useQueryClient();

    return useMutation<void, unknown, FranchisePair>({
        mutationFn: ({ franchiseDealerId, oemId }) => attachFranchise(franchiseDealerId, oemId),
        onSuccess: (_, pair) => invalidateFranchisePair(queryClient, pair),
    });
};

export const useDetachFranchiseMutation = () => {
    const queryClient = useQueryClient();

    return useMutation<void, unknown, FranchisePair>({
        mutationFn: ({ franchiseDealerId, oemId }) => detachFranchise(franchiseDealerId, oemId),
        onSuccess: (_, pair) => invalidateFranchisePair(queryClient, pair),
    });
};
